import type { JobStatus } from '@heidi/evidence/contracts';
import { getJobStatus, listUserDocuments } from '@heidi/evidence/data';

import type { PickedWorkflowContextDocument } from './LibraryDocumentPickerList';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface WorkflowContextDocumentWaitProgress {
  status: JobStatus;
}

type WorkflowContextDocumentWaitResult =
  | { kind: 'ready'; document: PickedWorkflowContextDocument }
  | { kind: 'failed' }
  | { kind: 'timed_out' }
  | { kind: 'aborted' };

/** Poll job status, then resolve the newest matching workflow_context filename. */
export async function waitForWorkflowContextDocument(
  jobId: string,
  filename: string,
  options?: {
    attempts?: number;
    intervalMs?: number;
    onProgress?: (progress: WorkflowContextDocumentWaitProgress) => void;
    signal?: AbortSignal;
  }
): Promise<WorkflowContextDocumentWaitResult> {
  const attempts = options?.attempts ?? 40;
  const intervalMs = options?.intervalMs ?? 2000;

  for (let i = 0; i < attempts; i += 1) {
    if (options?.signal?.aborted) return { kind: 'aborted' };
    const status = await getJobStatus(jobId, {
      signal: options?.signal,
      timeout: 10_000,
    });
    options?.onProgress?.({
      status: status.status,
    });

    if (status.status === 'failed') {
      return { kind: 'failed' };
    }
    if (status.status === 'completed') {
      const list = await listUserDocuments('user', 'workflow_context');
      const match = [...list.documents]
        .filter((d) => d.filename === filename)
        .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))[0];
      if (match) {
        return {
          kind: 'ready',
          document: { id: match.document_id, name: match.filename },
        };
      }
    }
    await sleep(Math.min(intervalMs * 2 ** Math.floor(i / 5), 10_000));
  }
  return { kind: 'timed_out' };
}
